import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2, Upload, Globe, Mail, Phone, MapPin,
  Facebook, Instagram, Twitter, Linkedin, Save, ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PROFILE = {
  company_name: '',
  description: '',
  logo_url: '',
  banner_url: '',
  website: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  country: '',
  social_facebook: '',
  social_instagram: '',
  social_twitter: '',
  social_linkedin: '',
};

export default function PartnerProfilePage() {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/partner/profile`, { withCredentials: true });
      if (res.data) {
        setProfile({ ...EMPTY_PROFILE, ...res.data });
        setHasProfile(true);
      }
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setter = field === 'logo_url' ? setUploadingLogo : setUploadingBanner;
    setter(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/upload/image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile((p) => ({ ...p, [field]: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setter(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile.company_name?.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      if (hasProfile) {
        await axios.put(`${API_URL}/api/partner/profile`, profile, { withCredentials: true });
        toast.success('Profile updated successfully');
      } else {
        await axios.post(`${API_URL}/api/partner/profile`, profile, { withCredentials: true });
        toast.success('Profile created successfully');
        setHasProfile(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  if (loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="h-32 skeleton rounded-xl" />
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="partner-profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-2">Partner Area</p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
            {hasProfile ? 'My Partner Profile' : 'Create Your Partner Profile'}
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            Showcase your brand with custom logo, banner, contact details &amp; social links.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-profile-form">
          {/* Banner */}
          <section className="dashboard-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
              Branding
            </h2>

            <div className="mb-5">
              <Label className="text-white/70 mb-2 block">Banner Image</Label>
              <div className="relative h-40 w-full rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10">
                {profile.banner_url ? (
                  <img src={profile.banner_url} alt="Banner" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <span className="text-sm">No banner uploaded</span>
                  </div>
                )}
                <label className="absolute bottom-3 right-3 cursor-pointer">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'banner_url')} className="hidden" data-testid="upload-banner-input" />
                  <span className="px-3 py-1.5 rounded-lg bg-[#D4AF37] text-[#0A0A0A] text-sm font-medium inline-flex items-center gap-1">
                    <Upload className="w-4 h-4" />
                    {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-5 items-start">
              <div>
                <Label className="text-white/70 mb-2 block">Logo</Label>
                <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10">
                  {profile.logo_url ? (
                    <img src={profile.logo_url} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                </div>
                <label className="cursor-pointer mt-2 inline-block">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo_url')} className="hidden" data-testid="upload-logo-input" />
                  <span className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1">
                    <Upload className="w-3 h-3" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </span>
                </label>
              </div>

              <div className="flex-1 space-y-4">
                <div>
                  <Label className="text-white/70">Company Name *</Label>
                  <Input
                    value={profile.company_name}
                    onChange={(e) => set('company_name', e.target.value)}
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white"
                    data-testid="partner-company-name"
                  />
                </div>
                <div>
                  <Label className="text-white/70">About / Description</Label>
                  <Textarea
                    value={profile.description || ''}
                    onChange={(e) => set('description', e.target.value)}
                    rows={3}
                    placeholder="Tell the world about your brand..."
                    className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                    data-testid="partner-description"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="dashboard-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Globe className="w-3.5 h-3.5" />Website</Label>
                <Input value={profile.website || ''} onChange={(e) => set('website', e.target.value)} placeholder="https://..." className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-website" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Mail className="w-3.5 h-3.5" />Email</Label>
                <Input type="email" value={profile.email || ''} onChange={(e) => set('email', e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-email" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Phone className="w-3.5 h-3.5" />Phone</Label>
                <Input value={profile.phone || ''} onChange={(e) => set('phone', e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-phone" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />Address</Label>
                <Input value={profile.address || ''} onChange={(e) => set('address', e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-address" />
              </div>
              <div>
                <Label className="text-white/70">City</Label>
                <Input value={profile.city || ''} onChange={(e) => set('city', e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-city" />
              </div>
              <div>
                <Label className="text-white/70">Country</Label>
                <Input value={profile.country || ''} onChange={(e) => set('country', e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-country" />
              </div>
            </div>
          </section>

          {/* Social */}
          <section className="dashboard-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#D4AF37]" />
              Social Links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Facebook className="w-3.5 h-3.5" />Facebook</Label>
                <Input value={profile.social_facebook || ''} onChange={(e) => set('social_facebook', e.target.value)} placeholder="https://facebook.com/..." className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-facebook" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Instagram className="w-3.5 h-3.5" />Instagram</Label>
                <Input value={profile.social_instagram || ''} onChange={(e) => set('social_instagram', e.target.value)} placeholder="https://instagram.com/..." className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-instagram" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Twitter className="w-3.5 h-3.5" />Twitter / X</Label>
                <Input value={profile.social_twitter || ''} onChange={(e) => set('social_twitter', e.target.value)} placeholder="https://twitter.com/..." className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-twitter" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2"><Linkedin className="w-3.5 h-3.5" />LinkedIn</Label>
                <Input value={profile.social_linkedin || ''} onChange={(e) => set('social_linkedin', e.target.value)} placeholder="https://linkedin.com/..." className="bg-[#0A0A0A] border-white/10 text-white" data-testid="partner-linkedin" />
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] font-semibold px-8" data-testid="partner-profile-save">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : hasProfile ? 'Save Changes' : 'Create Profile'}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
